import { Nullable } from '@aibees/shared/definition';
import type {
  Person,
  PersonCompany,
  PersonCompanyGeneralInfo,
  PersonPostionGroup,
  PersonProfilePosition,
} from '../feature/search-table-people/types';

export type CompanyType = PersonCompany | PersonCompanyGeneralInfo;

export type EmploymentType = 'Self-Employed' | 'Freelancer' | null;

type CompanyName = PersonCompanyGeneralInfo['name'];

export function normalizeEmploymentType(
  employmentType?: Nullable<PersonProfilePosition['employment_type']>
): EmploymentType {
  if (!employmentType) return null;
  const normalized = employmentType.trim().toLowerCase();
  if (normalized === 'self-employed') {
    return 'Self-Employed';
  }
  if (normalized === 'freelance') {
    return 'Freelancer';
  }
  return null;
}

function isCompanyNameNA(name: Nullable<CompanyName>): boolean {
  if (!name) return true;
  const trimmed = name.trim();
  return trimmed === '' || trimmed.toLowerCase() === 'n/a';
}

function isValidCompanyName(name: Nullable<CompanyName>): boolean {
  return !isCompanyNameNA(name);
}

function hasGeneralInfoCompanyOnlyName(
  company: PersonCompanyGeneralInfo
): boolean {
  return isValidCompanyName(company.name) && !company.logo && !company.url;
}

function hasGeneralInfoCompanyNoData(
  company: PersonCompanyGeneralInfo
): boolean {
  return isCompanyNameNA(company.name) && !company.logo && !company.url;
}

function hasOtherFieldsInPersonCompany(company: PersonCompany): boolean {
  if (!company.summary) return false;

  // Check if summary has other fields besides name
  const { name: _summaryName, ...restSummary } = company.summary;
  void _summaryName;
  const hasOtherSummaryFields = Object.values(restSummary).some(
    (value) => !!value
  );

  // Check if company has other fields besides summary
  const { id: _companyId, summary: _companySummary, ...restCompany } = company;
  void _companyId;
  void _companySummary;
  const hasOtherCompanyFields = Object.values(restCompany).some(
    (value) => !!value
  );

  return hasOtherSummaryFields || hasOtherCompanyFields;
}

function hasPersonCompanyOnlyName(company: PersonCompany): boolean {
  if (!isValidCompanyName(company.summary?.name ?? null)) return false;
  return !hasOtherFieldsInPersonCompany(company);
}

function hasPersonCompanyNoData(company: PersonCompany): boolean {
  if (!company.summary) return true;
  const companyName = company.summary.name ?? null;
  if (isValidCompanyName(companyName)) return false;
  return !hasOtherFieldsInPersonCompany(company);
}

export function hasPositionGroupCompanyOnlyName(
  company: Nullable<CompanyType>
): boolean {
  if (!company || company.id) return false;
  return 'name' in company && hasGeneralInfoCompanyOnlyName(company);
}

export function hasOnlyCompanyName(company: Nullable<CompanyType>): boolean {
  if (!company) return false;

  if ('summary' in company && company.summary) {
    return hasPersonCompanyOnlyName(company);
  }

  if ('name' in company) {
    return hasGeneralInfoCompanyOnlyName(company);
  }

  return false;
}

export function hasNoCompanyDataAvailable(
  company: Nullable<CompanyType>
): boolean {
  if (!company) return true;

  if ('summary' in company && company.summary) {
    return hasPersonCompanyNoData(company);
  }

  if ('name' in company) {
    return hasGeneralInfoCompanyNoData(company);
  }

  return true;
}

export function hasAllCompanyDataAvailable(
  company: Nullable<CompanyType>
): boolean {
  return !hasOnlyCompanyName(company) && !hasNoCompanyDataAvailable(company);
}

function checkCompanyFromBothSources(
  positionGroupCompany: Nullable<CompanyType>,
  mainCompany: Nullable<CompanyType>,
  validator: (company: Nullable<CompanyType>) => boolean,
  fallbackValue: boolean
): boolean {
  if (positionGroupCompany && mainCompany) {
    return validator(positionGroupCompany) && validator(mainCompany);
  }
  return positionGroupCompany
    ? validator(positionGroupCompany)
    : mainCompany
    ? validator(mainCompany)
    : fallbackValue;
}

export type CurrentCompanyState = {
  hasOnlyCompanyName: boolean;
  noCompanyDataAvailable: boolean;
  isSelfEmployedOrFreelancer: boolean;
  employmentType: EmploymentType;
};

const EMPTY_CURRENT_COMPANY_STATE: CurrentCompanyState = {
  hasOnlyCompanyName: false,
  noCompanyDataAvailable: false,
  isSelfEmployedOrFreelancer: false,
  employmentType: null,
};

export function getCurrentCompanyState(
  personData: Nullable<Person>,
  firstRole: Nullable<PersonPostionGroup>
): CurrentCompanyState {
  if (!firstRole) {
    return {
      ...EMPTY_CURRENT_COMPANY_STATE,
      noCompanyDataAvailable: true,
    };
  }

  if (firstRole.date?.end) {
    return EMPTY_CURRENT_COMPANY_STATE;
  }

  const companyName =
    personData?.company?.summary?.name || firstRole.company?.name || null;

  const roleEmploymentType = firstRole.profile_positions?.[0]?.employment_type;
  const normalizedEmploymentType = normalizeEmploymentType(roleEmploymentType);

  const isSelfEmployedOrFreelancer =
    isCompanyNameNA(companyName) && !!normalizedEmploymentType;
  const employmentType = isSelfEmployedOrFreelancer
    ? normalizedEmploymentType
    : null;

  const positionGroupCompany = firstRole.company as Nullable<CompanyType>;
  const mainCompany = personData?.company as Nullable<CompanyType>;

  return {
    hasOnlyCompanyName: checkCompanyFromBothSources(
      positionGroupCompany,
      mainCompany,
      hasOnlyCompanyName,
      isValidCompanyName(companyName)
    ),
    noCompanyDataAvailable: checkCompanyFromBothSources(
      positionGroupCompany,
      mainCompany,
      hasNoCompanyDataAvailable,
      hasNoCompanyDataAvailable(null)
    ),
    isSelfEmployedOrFreelancer,
    employmentType,
  };
}

export type PreviousCompanyState = {
  hasOnlyPreviousCompanyName: boolean;
  noPreviousCompanyDataAvailable: boolean;
  isNoLongerEmployed: boolean;
  wasPreviouslySelfEmployedOrFreelancer: boolean;
  previousEmploymentType: EmploymentType;
};

const EMPTY_PREVIOUS_COMPANY_STATE: PreviousCompanyState = {
  hasOnlyPreviousCompanyName: false,
  noPreviousCompanyDataAvailable: false,
  isNoLongerEmployed: false,
  wasPreviouslySelfEmployedOrFreelancer: false,
  previousEmploymentType: null,
};

export function getPreviousCompanyState(
  fullWorkHistory: Nullable<PersonPostionGroup[]>,
  allPresentRoles?: Nullable<PersonPostionGroup[]>
): PreviousCompanyState {
  if (
    allPresentRoles?.length ||
    !fullWorkHistory ||
    fullWorkHistory.length === 0
  ) {
    return EMPTY_PREVIOUS_COMPANY_STATE;
  }

  const rolesWithEndDates = fullWorkHistory
    .filter((role) => role.date?.end)
    .sort((a, b) => {
      const endDateA = a.date?.end;
      const endDateB = b.date?.end;
      if (!endDateA || !endDateB) return 0;
      return endDateB.localeCompare(endDateA);
    });

  const lastRole = rolesWithEndDates[0];
  if (!lastRole) {
    return EMPTY_PREVIOUS_COMPANY_STATE;
  }

  const lastCompany = lastRole.company ?? null;
  const companyName = lastCompany?.name ?? null;

  let wasPreviouslySelfEmployedOrFreelancer = false;
  let previousEmploymentType: EmploymentType = null;

  if (isCompanyNameNA(companyName)) {
    const employment = lastRole.profile_positions?.[0]?.employment_type;
    const normalizedEmploymentType = normalizeEmploymentType(employment);
    if (normalizedEmploymentType) {
      wasPreviouslySelfEmployedOrFreelancer = true;
      previousEmploymentType = normalizedEmploymentType;
    }
  }

  return {
    hasOnlyPreviousCompanyName: hasOnlyCompanyName(lastCompany),
    noPreviousCompanyDataAvailable: hasNoCompanyDataAvailable(lastCompany),
    isNoLongerEmployed: hasAllCompanyDataAvailable(lastCompany),
    wasPreviouslySelfEmployedOrFreelancer,
    previousEmploymentType,
  };
}
